import { Complaint } from "@features/cx/complaints/types";
import { Button } from "@shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Checkbox } from "@shared/ui/checkbox";
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  CheckSquare,
  FileText,
  Info,
  Paperclip,
  X,
} from "lucide-react";
import React from "react";
import { toast } from "@shared/ui/use-toast";
interface ClosureRequirements {
  customerConfirmation: boolean;
  qualityAssuranceDone: boolean;
}

interface ComplaintDetailsTabProps {
  request: Complaint;
  onResolve?: (closureRequirements: any) => void;
  onReject?: (requestId: string) => void;
  onRejectConfirm?: (reason: string) => void;
  closureRequirements?: any;
  setClosureRequirements?: any;
}

const ComplaintDetailsTab: React.FC<ComplaintDetailsTabProps> = ({
  request,
  onResolve,
  onReject,
  onRejectConfirm,
  closureRequirements,
  setClosureRequirements,
}) => {
  // Placeholder: These would come from additionalData or related API fields
  const additionalData = (request as any)?.additionalData || {};
  const evidence = (request as any)?.evidence || [];
  const attachments = (request as any)?.file || [];
  const incidentDate = (request as any)?.incidentDate || "";
  const dueDate = request?.slaSummary.slaDueDate;
  const isPastDue = dueDate && new Date(dueDate) < new Date();
  const daysDue = dueDate
    ? Math.max(
        0,
        Math.ceil(
          (new Date(dueDate).getTime() - new Date().getTime()) /
            (1000 * 60 * 60 * 24)
        )
      )
    : null;

  const currentStatus = request?.statusDisplay;

  const handleClosureRequirementChange = (
    requirement: keyof ClosureRequirements,
    checked: boolean
  ) => {
    setClosureRequirements((prev) => ({
      ...prev,
      [requirement]: checked,
    }));
  };

  const handleResolveRequest = () => {
    if (request?.utilitySupportRequest?.extraData?.requiresApproval) {
      if (
        closureRequirements.customerConfirmation &&
        closureRequirements.qualityAssuranceDone
      ) {
        if (onResolve) {
          onResolve(closureRequirements);
        }
      } else {
        toast({
          title: "Error",
          description:
            "Please complete all closure requirements before resolving the complaint.",
          variant: "destructive",
        });
      }
    } else {
      if (onResolve) {
        onResolve(closureRequirements);
      }
    }
  };

  const handleRejectRequest = () => {
    if (onReject) {
      onReject(request?.id);
    }
  };

  return (
    <Card className="p-0 border-none shadow-none">
      <CardContent>
        <div className="space-y-6">
          {/* Basic Information */}
          <div className="space-y-4 border border-gray-200 p-4 rounded-md">
            <h4 className="card-heading-text flex items-center gap-2">
              <FileText className="h-4 w-4" /> Basic Information
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="card-subheading-text">Complaint Type</p>
                <p className="text-sm">
                  {request?.utilitySupportRequest?.name}
                </p>
              </div>
              <div>
                <p className="card-subheading-text">Complaint Type Code</p>
                <p className="text-sm">{request?.requestNo}</p>
              </div>
              <div>
                <p className="card-subheading-text">Acknowledgment Time</p>
                <p className="text-sm">
                  {request?.utilitySupportRequest?.extraData?.acknowlegmentTime}{" "}
                  hours
                </p>
              </div>
              <div>
                <p className="card-subheading-text">Category</p>
                <p className="text-sm">
                  {request?.utilitySupportRequest?.supportRequestType}
                </p>
              </div>
              <div>
                <p className="card-subheading-text">Sub-Category</p>
                <p className="text-sm">
                  {request?.utilitySupportRequest?.supportRequestSubtype}
                </p>
              </div>
              <div>
                <p className="card-subheading-text">Response Time</p>
                <p className="text-sm">
                  {request?.utilitySupportRequest?.extraData?.responseTime}{" "}
                  hours
                </p>
              </div>
              <div>
                <p className="card-subheading-text">Date Submitted</p>
                <p className="text-sm">{request?.createdDate}</p>
              </div>
              <div>
                <p className="card-subheading-text">Resolution Time</p>
                <p className="text-sm">
                  {request?.utilitySupportRequest?.extraData?.resolutionTime}
                </p>
              </div>
              <div>
                <p className="card-subheading-text">Incident Date</p>
                <p className="text-sm">{request?.requestDate || "-"}</p>
              </div>
              <div>
                <p className="card-subheading-text">Due Date</p>
                <p className="text-sm">{dueDate || "-"}</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 rounded-md">
              {/* SLA Target */}
              <div className="border border-gray-200 p-4 rounded-md">
                <p className="card-heading-text">SLA Target</p>
                <div className="grid grid-cols-3 text-sm text-muted-foreground">
                  <div className="text-center">
                    <p className="mb-1">Acknowledgement Time</p>
                    <p className="text-foreground font-medium">
                      {request?.utilitySupportRequest?.extraData
                        ?.acknowlegmentTime ?? "NA"}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="mb-1">Response Time</p>
                    <p className="text-foreground font-medium">
                      {request?.utilitySupportRequest?.extraData
                        ?.responseTime ?? "NA"}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="mb-1">Resolution Time</p>
                    <p className="text-foreground font-medium">
                      {request?.utilitySupportRequest?.extraData
                        ?.resolutionTime ?? "NA"}
                    </p>
                  </div>
                </div>
              </div>

              {/* SLA Achieved */}
              <div className="border border-gray-200 p-4 rounded-md">
                <p className="card-heading-text">SLA Achieved</p>
                <div className="grid grid-cols-3 text-sm text-muted-foreground">
                  <div className="text-center">
                    <p className="card-subheading-text">Acknowledgement Time</p>
                    <p className="text-green-600 font-medium">
                      {request?.acknowledgementTimeAchieved ?? "Pending"}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="card-subheading-text">Response Time</p>
                    <p className="text-green-600 font-medium">
                      {request?.responseTime ?? "Pending"}
                    </p>
                  </div>

                  <div className="text-center">
                    <p className="card-subheading-text">Resolution Time</p>
                    <p className="text-green-600 font-medium">
                      {request?.resolutionTimeAchieved ?? "Pending"}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-2 rounded mt-4">
              <div className="flex items-center">
                <AlertCircle className="h-4 w-4 text-yellow-400" />
                <span className="ml-2 text-xs text-yellow-800">
                  {dueDate ? (
                    isPastDue ? (
                      <>This complaint is due in 0 days. It is past due!</>
                    ) : (
                      <>This complaint is due in {daysDue} days.</>
                    )
                  ) : (
                    <>Due date not available.</>
                  )}
                </span>
              </div>
            </div>
          </div>

          {/* Description & Resolution */}
          <div className="space-y-4 border border-gray-200 p-4 rounded-md">
            <h4 className="card-heading-text flex items-center gap-2">
              Description & Resolution
            </h4>
            <div>
              <p className="card-subheading-text">Description</p>
              <p className="text-sm bg-white p-2 rounded border mt-1">
                {additionalData.description || "No description provided."}
              </p>
            </div>
            <div>
              <p className="card-subheading-text">Expected Resolution</p>
              <p className="text-sm bg-white p-2 rounded border mt-1">
                {additionalData.expectedResolution ||
                  "No expected resolution provided."}
              </p>
            </div>
          </div>

          {/* Evidence & Attachments and Closure Requirements */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Evidence & Attachments */}
            <div className="space-y-4 border border-gray-200 p-4 rounded-md">
              <div>
                <p className="card-subheading-text">Attachments</p>
                <div className="flex flex-wrap gap-2 mt-1">
                  {/* Placeholder: Render attachments as links/buttons if available */}
                  {request?.file ? (
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex items-center gap-1"
                   
                    >
                      <a
                        href={request?.file}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Paperclip className="h-3 w-3" />
                        {request?.file?.split("/").pop()}
                      </a>
                    </Button>
                  ) : (
                    <span className="card-subheading-text">
                      No attachments.
                    </span>
                  )}
                </div>
              </div>
              <div>
                <p className="card-subheading-text mt-2">
                  Additional Information
                </p>
                <p className="text-sm bg-white p-2 rounded border mt-1">
                  {additionalData.additionalNotes ||
                    "No additional information."}
                </p>
              </div>
              <div className="bg-blue-50 border-l-4 border-blue-400 p-2 rounded mt-4 flex items-center">
                <Info className="h-4 w-4 text-blue-400" />
                <span className="ml-2 text-xs text-blue-800">
                  All attachments are securely stored and can only be accessed
                  by authorized personnel.
                </span>
              </div>
            </div>

            {/* Closure Requirements */}
            <div className="space-y-4 border border-gray-200 p-4 rounded-md">
              <h4 className="card-heading-text flex items-center gap-2 mb-0">
                <CheckSquare className="h-4 w-4" />
                Closure Requirements
              </h4>
              <p className="text-xs text-neutral-500 mt-0">
                Please complete the following requirements before marking this
                complaint as resolved:
              </p>
              <div className="space-y-3">
                <div className="flex items-start gap-2">
                  <Checkbox
                    id="customerConfirmation"
                    checked={
                      additionalData.closureRequirements?.customerConfirmation
                    }
                    onCheckedChange={(checked) =>
                      handleClosureRequirementChange(
                        "customerConfirmation",
                        checked as boolean
                      )
                    }
                  />
                  <label
                    htmlFor="customerConfirmation"
                    className="text-sm cursor-pointer"
                  >
                    <div className="font-medium">Customer Confirmation</div>
                    <p className="card-subheading-text">
                      Confirm that the customer has acknowledged the resolution
                      and is satisfied with the outcome.
                    </p>
                  </label>
                </div>

                <div className="flex items-start gap-2">
                  <Checkbox
                    id="qualityAssuranceDone"
                    checked={
                      additionalData.closureRequirements?.qualityAssuranceDone
                    }
                    onCheckedChange={(checked) =>
                      handleClosureRequirementChange(
                        "qualityAssuranceDone",
                        checked as boolean
                      )
                    }
                  />
                  <label
                    htmlFor="qualityAssuranceDone"
                    className="text-sm cursor-pointer"
                  >
                    <div className="font-medium">Quality Assurance Done</div>
                    <p className="card-subheading-text">
                      Verify that all quality checks have been completed and the
                      resolution meets company standards.
                    </p>
                  </label>
                </div>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-2 rounded mt-4 flex items-center">
                <AlertTriangle className="h-4 w-4 text-yellow-400" />
                <span className="ml-2 text-xs text-yellow-800">
                  Both requirements must be completed before the complaint can
                  be marked as resolved.
                </span>
              </div>
            </div>
          </div>


        </div>
      </CardContent>
    </Card>
  );
};

export default ComplaintDetailsTab;
